import { supabase } from "@/integrations/supabase/client";
import { getDeviceLabel } from "@/data/appLinks";

/**
 * Lightweight, anonymous product usage tracking.
 *
 * Purpose: answer "do people come back?" and "where do they drop out?".
 * No personal data is stored — only a random id kept in this browser.
 */

const VISITOR_KEY = "ab_visitor_id";
const FIRST_SEEN_KEY = "ab_first_seen";
const SESSION_KEY = "ab_session_id";

export type ProductEventName =
  | "page_view"
  | "wait_view"
  | "leave_by_used"
  | "app_cta_click"
  | "signup_start"
  | "signup_complete"
  | "pricing_view"
  | "save_airport"
  | "airport_switch"
  | "chat_open";

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID().replace(/-/g, "");
    }
  } catch {
    /* ignore */
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

function readStore(store: Storage | undefined, key: string): string | null {
  try {
    return store?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStore(store: Storage | undefined, key: string, value: string) {
  try {
    store?.setItem(key, value);
  } catch {
    /* ignore */
  }
}

function getVisitorId(): string | null {
  if (typeof window === "undefined") return null;
  let id = readStore(window.localStorage, VISITOR_KEY);
  if (!id) {
    id = randomId();
    writeStore(window.localStorage, VISITOR_KEY, id);
    writeStore(window.localStorage, FIRST_SEEN_KEY, new Date().toISOString());
  }
  return id;
}

function getSessionId(): string | null {
  if (typeof window === "undefined") return null;
  let id = readStore(window.sessionStorage, SESSION_KEY);
  if (!id) {
    id = randomId();
    writeStore(window.sessionStorage, SESSION_KEY, id);
  }
  return id;
}

/** True when this browser was first seen on an earlier day. */
export function isReturningVisitor(): boolean {
  if (typeof window === "undefined") return false;
  const first = readStore(window.localStorage, FIRST_SEEN_KEY);
  if (!first) return false;
  const firstDay = new Date(first).toDateString();
  return firstDay !== new Date().toDateString();
}

/** Fire-and-forget. Never blocks the UI and never throws. */
export function trackEvent(event: ProductEventName, context?: string) {
  try {
    if (typeof window === "undefined") return;
    const visitor_id = getVisitorId();
    if (!visitor_id) return;

    void supabase
      .from("product_events" as any)
      .insert({
        visitor_id,
        session_id: getSessionId(),
        event,
        context: context ? context.slice(0, 80) : null,
        path: window.location.pathname.slice(0, 200),
        device: getDeviceLabel(),
        is_returning: isReturningVisitor(),
      } as any)
      .then(
        () => undefined,
        () => undefined,
      );
  } catch {
    /* ignore */
  }
}
